import React, { FC, useEffect } from 'react'
import isAfter from 'date-fns/isAfter'
import { SmallHeadline, SubDescription, SubHeadline } from 'components/Typography'
import { Spacer } from 'components/Utility'
import { SelectBox, DaysOfWeekPicker } from 'components/Form'
import { parseTimeToDate, parseToCircleNumber } from 'lib/helper'
import { useLastPickupTimes, useRelativePickupableTimes, useTimesWithStartTime } from 'hooks/store'
import { DayOfWeekType } from 'lib/daysOfWeek'
import { useForwardLastOrderTime, useTimeOptions } from 'hooks/businessHour'
import { Card, CardInner } from 'components/Card'
import { FlexRow } from 'components/Flex'
import { SelectDaysOfWeekButton } from 'components/Button'
import { BusinessTimesKey } from 'types/store'

type Props = {
  index: number
  daysOfWeek: DayOfWeekType[]
  openTime: string
  orderStartTime: string
  lastOrderTime: string
  lastPickupTime: string
  relativePickupableTime: string
  handleBusinessTimes: (index: number, key: BusinessTimesKey, value: string) => void
  selectDaysOfWeek: (dayOfWeek: DayOfWeekType, index: number) => void
  selectableDaysOfWeek: DayOfWeekType[]
  selectAll: () => void
  unselectAll: () => void
}

const BusinessHourForm: FC<Props> = (props) => {
  const {
    index,
    daysOfWeek,
    openTime,
    orderStartTime,
    lastOrderTime,
    lastPickupTime,
    relativePickupableTime,
    handleBusinessTimes,
    selectDaysOfWeek,
    selectableDaysOfWeek,
    selectAll,
    unselectAll,
  } = props

  // オープン時刻+15分が、ラストオーダー時刻以降であればラストオーダー時刻を更新する
  useForwardLastOrderTime(orderStartTime, lastOrderTime, (time: string) =>
    handleBusinessTimes(index, 'last_order_time', time)
  )

  // ラストオーダー時刻が最終受取時刻より遅い時刻であれば最終受取時刻を更新する
  useEffect(() => {
    if (isAfter(parseTimeToDate(lastOrderTime), parseTimeToDate(lastPickupTime))) {
      handleBusinessTimes(index, 'last_pickup_time', lastOrderTime)
    }
  }, [lastOrderTime])

  const openTimes = useTimeOptions()
  const orderStartTimes = useTimeOptions()
  // オープン時刻 < ラストオーダー時刻となる時間を選べるよう時間のオプションを生成
  const lastOrderTimes = useTimesWithStartTime(orderStartTime)
  // ラストオーダー時刻 <= 最終受渡時刻となる時間を選べるよう時間のオプションを生成
  const lastPickupTimes = useLastPickupTimes(lastOrderTime, orderStartTime)
  // 来店予定時刻の制限（当日取り置きのみ）のオプションを生成
  const relativePickupableTimes = useRelativePickupableTimes()

  return (
    <Card>
      <CardInner>
        <FlexRow justifyContent={'between'}>
          <SmallHeadline label={`営業時間${parseToCircleNumber(index + 1)}`} required={index === 0} />
          <SelectDaysOfWeekButton daysOfWeek={daysOfWeek} selectAll={selectAll} unselectAll={unselectAll} />
        </FlexRow>
        <Spacer size={8} />
        <SubDescription description={'営業時間を入力する曜日を選択してください（複数選択可）'} />
        <Spacer size={8} />
        <DaysOfWeekPicker
          selectableDaysOfWeek={selectableDaysOfWeek}
          daysOfWeek={daysOfWeek}
          index={index}
          selectDaysOfWeek={(dayOfWeek: DayOfWeekType) => selectDaysOfWeek(dayOfWeek, index)}
          wrap={false}
        />
        <Spacer size={16} />
        {/* オーダー開始時刻 */}
        <SubHeadline headline={'オーダー開始時刻'} id={`order-start-time-${index + 1}`} />
        <Spacer size={8} />
        <SubDescription description={'当日取り置き注文を受け付ける時刻を設定してください'} />
        <Spacer size={8} />
        <SelectBox
          options={orderStartTimes}
          select={(value: string) => handleBusinessTimes(index, 'order_start_time', value)}
          value={orderStartTime}
        />
        <Spacer size={16} />
        {/* 店舗オープン時刻 */}
        <SubHeadline headline={'店舗オープン時刻'} id={`open-time-${index + 1}`} />
        <Spacer size={8} />
        <SubDescription description={'お店が開店し商品を受け渡しできる時刻を設定してください'} />
        <Spacer size={8} />
        <SelectBox
          options={openTimes}
          select={(value: string) => handleBusinessTimes(index, 'open_time', value)}
          value={openTime}
        />
        <Spacer size={16} />
        {/* ラストオーダー時刻 */}
        <SubHeadline headline={'ラストオーダー時刻'} id={`last-order-time-${index + 1}`} />
        <Spacer size={8} />
        <SubDescription description={'当日取り置き注文を締め切る時刻を設定してください'} />
        <Spacer size={8} />
        <SelectBox
          options={lastOrderTimes}
          select={(value: string) => handleBusinessTimes(index, 'last_order_time', value)}
          value={lastOrderTime}
        />
        <Spacer size={16} />
        {/* 最終受渡時刻 */}
        <SubHeadline headline={'最終受渡時刻'} id={`last-pickup-time-${index + 1}`} />
        <Spacer size={8} />
        <SubDescription description={'商品の受け渡しができる最終時刻を設定してください'} />
        <Spacer size={8} />
        <SelectBox
          options={lastPickupTimes}
          select={(value: string) => handleBusinessTimes(index, 'last_pickup_time', value)}
          value={lastPickupTime}
        />
        <Spacer size={16} />
        {/* 来店予定時刻の制限（当日取り置きのみ） */}
        <SubHeadline headline={'来店予定時刻の制限（当日取り置きのみ）'} id={`relative-pickup-time-${index + 1}`} />
        <Spacer size={8} />
        <SubDescription
          description={
            'お客さまが、当日取り置き商品を購入する際に選択する来店予定時刻を、購入時刻よりN分後以降に選択してもらうことができます。注文からお客さまの来店まで猶予を保たせたい場合に有効です。'
          }
        />
        <Spacer size={8} />
        <SelectBox
          options={relativePickupableTimes}
          select={(value: string) => handleBusinessTimes(index, 'relative_pickupable_time', value)}
          value={relativePickupableTime}
        />
      </CardInner>
    </Card>
  )
}

export default BusinessHourForm
